import { supabase } from '../../lib/supabase';

import type { ResultBlock, ResultReviewItem, ResultWritingTask } from './result-types';
import type {
  ResultBands,
  ResultComments,
  ResultItemFeedback,
  ResultSpeaking,
} from './ResultDetail';

// `rpcUnknown` escape hatch, as every phase since 12: the RPC is not in the
// generated types until the user re-runs `gen types`.
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

/** The student's own published result for one attempt — the whole payload
 *  `attempt_review_payload` builds. */
export interface AttemptReview {
  result: ResultBands | null;
  review: ResultReviewItem[];
  /** Phase 188 — the shared stimulus behind the review's block blanks. */
  blocks?: ResultBlock[];
  items: ResultItemFeedback[];
  comments: ResultComments;
  writing?: ResultWritingTask[];
  speaking?: ResultSpeaking | null;
}

export const attemptReviewKey = (attemptId: string) => ['student', 'result', attemptId] as const;

/**
 * Lives in `shared/` (Phase 188) rather than beside the student results page,
 * because two features now read the same payload and a feature may not import
 * a sibling: the results page renders it as a report, and the read-only paper
 * review joins its verdicts onto the delivery payload by `question_id`.
 *
 * Throws when results are not published — that gate is in the RPC, not here.
 */
export async function getAttemptReview(attemptId: string): Promise<AttemptReview> {
  const { data, error } = await rpcUnknown.rpc('student_view_result', {
    p_attempt_id: attemptId,
  });
  if (error !== null) throw new Error(error.message);
  if (data === null || typeof data !== 'object') throw new Error('no result');
  return data as AttemptReview;
}
